import { useState, useEffect, useCallback, useRef } from 'react';
import { popupState } from '@/lib/popupState';

/** Valid popup type identifiers. */
const POPUP_TYPES = ['behavioral', 'exit-intent'] as const;
type PopupType = (typeof POPUP_TYPES)[number];

interface UsePopupManagerReturn {
  /** The popup currently being shown, or null if none */
  activePopup: PopupType | null;
  /** Request to show a popup — only succeeds if no popup has been shown this session and state allows it */
  requestPopup: (type: PopupType) => boolean;
  /**
   * Force-show a popup, bypassing global isBlocked and session guards.
   * Used for popups with independent suppression.
   * Still prevents simultaneous display — returns false if another popup is active.
   */
  forceRequestPopup: (type: PopupType) => boolean;
  /** Dismiss the active popup (14-day suppression) */
  dismissPopup: () => void;
  /** Record a conversion on the active popup (permanent suppression) */
  convertPopup: () => void;
  /** Whether popups are globally blocked (converted or within suppression window) */
  isBlocked: boolean;
  /** Whether this is the visitor's first ever session (no prior localStorage state) */
  isFirstSession: boolean;
}

/**
 * Central manager for popup display. Enforces:
 * - One popup per session (whichever triggers first wins)
 * - 14-day suppression after dismissal
 * - Permanent suppression after conversion
 *
 * Individual popup hooks (behavioral, exit-intent) call
 * `requestPopup(type)` when their trigger condition is met.
 * The manager decides whether to grant or deny the request.
 */
function usePopupManager(): UsePopupManagerReturn {
  const [activePopup, setActivePopup] = useState<PopupType | null>(null);
  // Initializes to true to prevent any trigger from firing synchronously before
  // the useEffect below reads the persisted state on mount.
  const [isBlocked, setIsBlocked] = useState(true);
  const shownThisSession = useRef(false);
  // Captured before ensureFirstVisit() writes to localStorage, so it reflects
  // whether the user had any prior state before this page load.
  const isFirstSessionRef = useRef(popupState.isFirstVisit());

  useEffect(() => {
    popupState.ensureFirstVisit();
    setIsBlocked(popupState.isBlocked());
  }, []);

  const requestPopup = useCallback(
    (type: PopupType): boolean => {
      if (shownThisSession.current) return false;
      if (popupState.isBlocked()) return false;

      shownThisSession.current = true;
      setActivePopup(type);
      return true;
    },
    [],
  );

  const forceRequestPopup = useCallback(
    (type: PopupType): boolean => {
      if (activePopup !== null) return false;
      setActivePopup(type);
      return true;
    },
    [activePopup],
  );

  const dismissPopup = useCallback(() => {
    if (!activePopup) return;
    popupState.recordDismissal(activePopup);
    setActivePopup(null);
    setIsBlocked(true);
  }, [activePopup]);

  const convertPopup = useCallback(() => {
    if (!activePopup) return;
    popupState.recordConversion(activePopup);
    setActivePopup(null);
    setIsBlocked(true);
  }, [activePopup]);

  return {
    activePopup,
    requestPopup,
    forceRequestPopup,
    dismissPopup,
    convertPopup,
    isBlocked,
    isFirstSession: isFirstSessionRef.current,
  };
}

export { usePopupManager };
export type { PopupType };
